import { useQuery, useQueryClient } from "@tanstack/react-query";

import OrganizationFunctionsService from "src/modules/data-protection/apis/OrganizationFunctionsService";
import {
    ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY,
    RESERVED_FUNCTION_KEYS,
    extractOrganizationFunction,
    getOrganizationFunctionQueryKey,
    toastOrganizationFunctionError,
} from "src/modules/data-protection/constants/organizationFunctions";

import { getAssignedDpoUser, getDpoSyncAction } from "../helpers/dpoFunctionSync";

const DPO_KEY = RESERVED_FUNCTION_KEYS.DPO;

const useDpoFunctionSync = (t) => {
    const queryClient = useQueryClient();

    const dpoQuery = useQuery({
        queryKey: getOrganizationFunctionQueryKey(DPO_KEY),
        queryFn: async () => {
            const service = OrganizationFunctionsService.getInstance();
            const data = await service.show(DPO_KEY);

            return extractOrganizationFunction(data);
        },
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while fetching function details."
            );
        },
    });

    const invalidateDpoQueries = () => {
        queryClient.invalidateQueries(getOrganizationFunctionQueryKey(DPO_KEY));
        queryClient.invalidateQueries(ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY);
    };

    const syncDpoAssignment = async (pickerUserId) => {
        const action = getDpoSyncAction({
            currentAssignedUserId: getAssignedDpoUser(dpoQuery.data)?.id,
            pickerUserId,
        });

        if (action.type === "skip") {
            return action;
        }

        const service = OrganizationFunctionsService.getInstance();

        try {
            if (action.type === "unassign") {
                await service.unassign(DPO_KEY, action.userId);
            } else if (action.type === "assign") {
                await service.assign(DPO_KEY, action.userId);
            } else if (action.type === "replace") {
                await service.replaceDpo(action.userId);
            }

            invalidateDpoQueries();
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while updating Function assignments."
            );

            throw error;
        }

        return action;
    };

    return {
        dpoFunction: dpoQuery.data,
        isDpoFunctionLoading: dpoQuery.isFetching,
        syncDpoAssignment,
        assignedDpoUser: getAssignedDpoUser(dpoQuery.data),
    };
};

export default useDpoFunctionSync;
